import { gqlFetcher, graphql } from '#features/api';
import { FilterInput, OrderInput } from '#features/api/types';

const blogPostsDoc = graphql(`
  query BlogPosts($offset: Int, $limit: Int, $orders: [OrderInput], $filters: [FilterInput]) {
    blogs {
      list(offset: $offset, limit: $limit, orders: $orders, filters: $filters) {
        blogs {
          id
          content
          createdAt
          ogDescription
          title
          date
          previewImage
          slug
          tags
        }
        hasMore
      }
    }
  }
`);

export const getBlogPosts = async (input: {
  offset: number;
  limit: number;
  orders?: OrderInput[];
  filters?: FilterInput[];
}) => {
  const data = await gqlFetcher(blogPostsDoc, {
    offset: input.offset,
    limit: input.limit,
    orders: input.orders,
    filters: input.filters,
  });

  return { records: data.blogs?.list?.blogs ?? [], hasMore: data.blogs?.list?.hasMore };
};

export const blogPostsQueryOptions = (input: {
  offset: number;
  limit: number;
  orders?: OrderInput[];
  filters?: FilterInput[];
}) => {
  return {
    queryKey: ['blogPosts', input.offset, input.limit, input.orders, input.filters],
    queryFn: () => getBlogPosts(input),
  };
};

const blogPostBySlugDoc = graphql(`
  query BlogPost($slug: String!) {
    blogs {
      findBySlug(slug: $slug) {
        content
        date
        updatedAt
        id
        keywords
        ogDescription
        ogTitle
        previewImage
        tags
        title
      }
    }
  }
`);

export const getBlogPostBySlug = async (slug: string) => {
  const data = await gqlFetcher(blogPostBySlugDoc, { slug });
  return data.blogs?.findBySlug ?? null;
};

export const blogPostBySlugQueryOptions = (slug: string) => {
  return {
    queryKey: ['blogPost', slug],
    queryFn: () => getBlogPostBySlug(slug),
  };
};

/**
 * Only `__typename` is selected: the caller ignores the result, so this keeps the selection
 * minimal. Note the union must not be narrowed with an inline fragment (`... on Operation`) —
 * the gateway's RequestContextInjector ClassCasts selections to Field (see postOgFragment).
 */
const incrementBlogViewDoc = graphql(`
  mutation IncrementBlogView($id: UUID!) {
    blog {
      incrementView(id: $id) {
        __typename
      }
    }
  }
`);

/**
 * Counts one view of a blog post. Fire-and-forget by contract: this never rejects and never
 * returns anything, so a failing counter can never break reading an article. A view is a
 * nice-to-have metric; the article is the product.
 */
export const incrementBlogView = async (id: string): Promise<void> => {
  try {
    await gqlFetcher(incrementBlogViewDoc, { id });
  } catch {
    // Swallowed on purpose — see above. Not reported to Sentry either: gqlFetcher already
    // reports genuine network faults, and a noisy counter would add nothing but alert fatigue.
  }
};
